import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { resolveConfig, type ResolvedWidgetConfig } from "./config";
import { WidgetController } from "./controller";
import type { WidgetState } from "./state";
import { sharedStyles } from "../styles/shared";
import "../components/message-list";
import "../components/message-bubble";
import "../components/suggestions";
import "../components/composer";
import "../components/recent-chats";
import "../components/overflow-menu";
import "../components/visitor-details-screen";
import "../components/scheduling-slots";
import "../components/action-widgets";
import { bindViewportUnit } from "../utils/viewport";
import { resolveTheme, applyTheme } from "../utils/theme";
import { injectInterFont } from "../utils/fonts";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import { isDomainAllowed, getWidgetHost } from "../utils/auth";

export type ChatgrowTriggerSource = "api" | "attribute" | "iframe";
type PendingExternalCommand = {
  command: "open" | "toggle";
  source: ChatgrowTriggerSource;
};

@customElement("chatgrow-widget")
export class ChatGrowWidget extends LitElement {
  static override styles = [sharedStyles];

  private authorized: boolean | null = null;

  private activeTheme?: "light" | "dark";

  private unbindViewport?: () => void;
  private themeMq?: MediaQueryList | undefined;
  private themeMqHandler?: (() => void) | undefined;
  private bodyOverflowBeforeLock: string | null = null;
  private pendingExternalCommand: PendingExternalCommand | undefined;

  private config!: ResolvedWidgetConfig;
  private controller!: WidgetController;
  private unsub?: (() => void) | undefined;

  private onOnline = () => {
    this.controller?.setOnline(true);
    this.controller?.clearError?.();
  };
  private onOffline = () => this.controller?.setOnline(false);
  private onHostMessage = (event: MessageEvent<unknown>) => {
    if (window.self === window.top || event.source !== window.parent) return;
    if (this.authorized !== true || !this.s?.agent) return;

    let parentHostname: string;
    try {
      parentHostname = new URL(event.origin).hostname;
    } catch {
      return;
    }

    if (!isDomainAllowed(this.s.agent.allowedDomains, parentHostname)) return;
    if (!event.data || typeof event.data !== "object") return;

    const type = (event.data as { type?: unknown }).type;
    let opened = false;

    if (type === "CHATGROW_OPEN") {
      opened = this.open("iframe");
    } else if (type === "CHATGROW_CLOSE") {
      this.close();
    } else if (type === "CHATGROW_TOGGLE") {
      opened = this.toggle("iframe");
    } else {
      return;
    }

    if (opened) {
      window.parent.postMessage(
        { type: "CHATGROW_OPENED", source: "custom-trigger" },
        event.origin,
      );
    }
  };

  @state() private salutation: String | undefined = "";

  @state() private s?: WidgetState;

  override connectedCallback(): void {
    super.connectedCallback();

    injectInterFont(this.shadowRoot ?? this);

    this.config = resolveConfig(this);
    if (this.config.defaultOpen) {
      this.setAttribute("default-open", "true");
    }
    this.controller = new WidgetController(this.config, this);

    this.salutation = this.pickSalutation();

    window.addEventListener("online", this.onOnline);
    window.addEventListener("offline", this.onOffline);
    window.addEventListener("message", this.onHostMessage);
    this.controller.setOnline(navigator.onLine);

    const resolved = resolveTheme(this.config.theme);
    this.activeTheme = resolved;
    applyTheme(this, resolved);

    // React to system theme changes when auto
    if (this.config.theme === "AUTO") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => {
        const next = resolveTheme("AUTO");
        if (next !== this.activeTheme) {
          this.activeTheme = next;
          applyTheme(this, next);
        }
      };
      mq.addEventListener("change", handler);
    }

    this.unsub = this.controller.subscribe((st) => {
      this.s = st;
      this.syncPageScroll(st.open);

      // Ensure error state is visible
      if (st.error && this.authorized !== true) {
        this.authorized = true;
        this.flushPendingExternalCommand();
        this.requestUpdate();
        return;
      }

      // Wait until agent loads and auth not yet resolved
      if (!st.agent || this.authorized !== null) return;

      // Determine the hostname for domain authorization
      const hostname = getWidgetHost();

      const allowed =
        st.agent.websiteIntegrationEnabled === true &&
        isDomainAllowed(st.agent.allowedDomains, hostname);

      if (!allowed) {
        this.authorized = false;
        this.pendingExternalCommand = undefined;
        this.syncPageScroll(false);

        console.error(
          `[ChatGrow] Widget blocked:
       - Domain: "${hostname}"
       - Allowed Domains: ${JSON.stringify(st.agent.allowedDomains)}
       - Website Integration Enabled: ${st.agent.websiteIntegrationEnabled}
       - Agent: "${st.agent.name}"`,
        );

        // Kill everything safely
        this.controller?.stream.stop();
        this.unsub?.();
        this.unsub = undefined;

        this.requestUpdate();
        return;
      }

      // ✅ Authorized
      this.authorized = true;
      this.applyAgentTheme(st.agent);
      this.notifyParentReady();
      this.flushPendingExternalCommand();
      this.requestUpdate();
    });

    // init async
    void this.controller.init();

    this.unbindViewport = bindViewportUnit(this);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsub?.();
    this.unsub = undefined;
    this.controller?.stream.stop();
    this.unbindViewport?.();
    window.removeEventListener("message", this.onHostMessage);
    this.syncPageScroll(false);

    if (this.themeMq && this.themeMqHandler) {
      this.themeMq.removeEventListener("change", this.themeMqHandler);
    }
    this.themeMq = undefined;
    this.themeMqHandler = undefined;
  }

  private syncPageScroll(open: boolean): void {
    const shouldLock =
      open &&
      !this.config.defaultOpen &&
      window.self === window.top &&
      window.matchMedia("(max-width: 480px)").matches;

    if (shouldLock && this.bodyOverflowBeforeLock === null) {
      this.bodyOverflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else if (!shouldLock && this.bodyOverflowBeforeLock !== null) {
      document.body.style.overflow = this.bodyOverflowBeforeLock;
      this.bodyOverflowBeforeLock = null;
    }
  }

  get isOpen(): boolean {
    return this.controller?.snapshot.open ?? false;
  }

  open(source: ChatgrowTriggerSource = "api"): boolean {
    if (!this.controller) return false;
    if (this.authorized === null) {
      this.pendingExternalCommand = { command: "open", source };
      return false;
    }
    if (this.authorized !== true || this.controller.snapshot.open) return false;

    this.controller.open();
    this.announceCustomOpen(source);
    return true;
  }

  close(): boolean {
    this.pendingExternalCommand = undefined;
    if (!this.controller || !this.controller.snapshot.open) return false;
    this.controller.close();
    return true;
  }

  toggle(source: ChatgrowTriggerSource = "api"): boolean {
    if (!this.controller) return false;
    if (this.authorized === null) {
      this.pendingExternalCommand = this.pendingExternalCommand
        ? undefined
        : { command: "toggle", source };
      return false;
    }
    if (this.authorized !== true) return false;

    if (this.controller.snapshot.open) return this.close();
    return this.open(source);
  }

  private flushPendingExternalCommand(): void {
    const pending = this.pendingExternalCommand;
    this.pendingExternalCommand = undefined;
    if (!pending) return;

    if (pending.command === "open") {
      this.open(pending.source);
    } else {
      this.toggle(pending.source);
    }
  }

  private announceCustomOpen(source: ChatgrowTriggerSource): void {
    const detail = { source };
    window.dispatchEvent(
      new CustomEvent("widget_opened_via_custom_button", { detail }),
    );
    this.dispatchEvent(
      new CustomEvent("chatgrow:opened", {
        bubbles: true,
        composed: true,
        detail,
      }),
    );
  }

  private notifyParentReady(): void {
    if (window.self === window.top || !document.referrer) return;

    try {
      const parentOrigin = new URL(document.referrer).origin;
      window.parent.postMessage({ type: "CHATGROW_READY" }, parentOrigin);
    } catch {
      // A missing or invalid referrer cannot be used as a secure target origin.
    }
  }

  private panelPosition(): {
    bottom: string;
    left?: string;
    right?: string;
  } {
    const base = { bottom: "calc(88px + env(safe-area-inset-bottom))" };

    const agentPos = this.s?.agent?.widgetPosition;
    const isLeft =
      agentPos === "BOTTOM_LEFT" ||
      (!agentPos && this.config.position === "bottom-left");

    if (isLeft) return { ...base, left: "12px" };
    return { ...base, right: "12px" };
  }

  private applyAgentTheme(agent?: {
    primaryColor?: string;
    textColor?: string;
    chatBubbleColor?: string;
    appearance?: "LIGHT" | "DARK" | "AUTO" | null;
  }) {
    if (!agent) return;

    const host = this;

    // Apply primary color to CSS variable for usage in header/launcher
    if (agent.primaryColor) {
      host.style.setProperty("--cg-primary", agent.primaryColor);
    }

    if (agent.textColor) {
      host.style.setProperty("--cg-on-primary", agent.textColor);
      host.style.setProperty("--cg-chat-text", agent.textColor);
    }

    if (agent.chatBubbleColor) {
      host.style.setProperty("--cg-chat-bubble", agent.chatBubbleColor);
    }

    // Handle appearance (Light/Dark)
    // Only override if config is set to 'auto' (default)
    if (this.config.theme === "AUTO" && agent.appearance) {
      const mode = agent.appearance === "DARK" ? "dark" : "light";

      // Force apply the theme based on the mode
      if (mode === "dark") {
        host.style.setProperty("--cg-bg", "#0b0f19");
        host.style.setProperty("--cg-surface", "rgba(18,24,38,0.92)");

        // Requested Dark Mode Colors
        host.style.setProperty("--cg-text", "rgb(243 244 246 / 1)"); // Matches agent text
        host.style.setProperty("--cg-muted", "rgba(249,250,251,0.55)");
        host.style.setProperty("--cg-border", "rgb(31 41 55 / 1)");
        host.style.setProperty("--cg-close-border", "rgb(31 41 55 / 1)");

        // Bubbles
        host.style.setProperty("--cg-bubble-agent-bg", "rgb(31 41 55 / 1)");
        host.style.setProperty(
          "--cg-bubble-agent-text",
          "rgb(243 244 246 / 1)",
        );
        host.style.setProperty(
          "--cg-bubble-user-bg",
          "oklch(96.74% .0013 286.37)",
        );
        host.style.setProperty(
          "--cg-bubble-user-text",
          "oklch(14.1% .005 285.823)",
        );

        host.style.setProperty(
          "--cg-shadow",
          "0 12px 40px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.2)",
        );
      } else {
        host.style.setProperty("--cg-bg", "#ffffff");
        host.style.setProperty("--cg-surface", "rgba(255,255,255,0.94)");

        // Requested Light Mode Colors
        host.style.setProperty("--cg-text", "oklch(14.1% .005 285.823)"); // Matches agent text
        host.style.setProperty("--cg-muted", "rgba(17,24,39,0.6)");
        host.style.setProperty("--cg-border", "rgba(17,24,39,0.12)");
        host.style.setProperty(
          "--cg-close-border",
          "rgba(255, 255, 255, 0.94)",
        );

        // Bubbles
        host.style.setProperty(
          "--cg-bubble-agent-bg",
          "rgba(255,255,255,0.94)",
        );
        host.style.setProperty(
          "--cg-bubble-agent-text",
          "oklch(14.1% .005 285.823)",
        );
        host.style.setProperty(
          "--cg-bubble-user-bg",
          "oklch(14.1% .005 285.823)",
        );
        host.style.setProperty("--cg-bubble-user-text", "oklch(100% 0 0)");

        host.style.setProperty(
          "--cg-shadow",
          "0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.08)",
        );
      }

      this.activeTheme = mode;
    }
  }

  private get headerState() {
    if (this.s?.loading) return "loading";
    if (this.s?.error || !this.s?.agent) return "error";
    return "ready";
  }

  private pickSalutation() {
    const list = this.config.salutations ?? [];
    if (!list.length) return "";

    const index = Math.floor(Math.random() * list.length);
    return list[index];
  }

  override render() {
    // Prevent any rendering until domain authorization resolves
    if (this.authorized !== true) return nothing;

    const s = this.s;
    if (!s) return html``;

    const isBusy = !!s.streaming?.active || s.sending;

    const pos = this.panelPosition();
    const launcherStyle = `bottom: 12px; ${pos.right ? "right: 12px;" : "left: 12px;"} ${this.config.defaultOpen ? "display: none;" : ""}`;
    const panelStyle = this.config.defaultOpen
      ? ""
      : `bottom: ${pos.bottom}; ${pos.right ? "right: 12px;" : "left: 12px;"}`;
    const popupStyle = `${pos.right ? "right: 12px;" : "left: 12px;"}`;

    return html`
      <button
        class="launcher ${s.open ? "launcher-shifted" : ""}"
        style=${launcherStyle}
        @click=${() => this.controller.toggle()}
      >
        ${s.open
          ? html`
                <!-- Close / Dropdown Icon -->
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-chevron-down"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              `
          : html`
                <!-- Default Icon -->
                <svg
                  width="28"
                  height="32"
                  viewBox="0 0 35 41"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="35" height="37" rx="4" fill="white" />
                  <path d="M27 37L35 41L35 33L27 37Z" fill="white" />
                  <path
                    d="M18.0682 14.035C18.0777 13.9883 18.1444 13.9883 18.154 14.035C18.6491 16.4577 20.5423 18.3509 22.965 18.846C23.0116 18.8555 23.0116 18.9223 22.965 18.9318C20.5423 19.4269 18.6491 21.3202 18.154 23.7429C18.1444 23.7894 18.0777 23.7894 18.0682 23.7429C17.573 21.3202 15.6798 19.4269 13.2572 18.9318C13.2105 18.9223 13.2105 18.8555 13.2572 18.846C15.6798 18.3509 17.573 16.4577 18.0682 14.035Z"
                    fill="black"
                  />
                  <path
                    d="M13.2115 14.0088C13.2139 13.9971 13.2306 13.9971 13.233 14.0088C13.3567 14.6144 13.83 15.0877 14.4357 15.2115C14.4474 15.2139 14.4474 15.2306 14.4357 15.233C13.83 15.3567 13.3567 15.83 13.233 16.4357C13.2306 16.4474 13.2139 16.4474 13.2115 16.4357C13.0877 15.83 12.6144 15.3567 12.0088 15.233C11.9971 15.2306 11.9971 15.2139 12.0088 15.2115C12.6144 15.0877 13.0877 14.6144 13.2115 14.0088Z"
                    fill="black"
                  />
                </svg>
              `}
      </button>
      ${!s.open && s.showInitialMessage && s.agent?.initialMessages?.length
        ? html`
            <div
              class="initial-message-popup ${pos.right ? "right" : "left"}"
              style=${popupStyle}
              @click=${() => this.controller.toggle()}
            >
              <button
                class="initial-message-close"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  this.controller.dismissInitialMessage();
                }}
              >
                ✕
              </button>

              ${s.agent.initialMessages.map(
                (msg: string) => html`
                  <div class="initial-message-bubble">${msg}</div>
                `,
              )}
            </div>
          `
        : nothing}
      ${s.open
        ? html`
            <div
              class="panel ${this.config.defaultOpen ? "embedded" : ""}"
              style=${panelStyle}
              role="dialog"
              aria-label="ChatGrow widget"
            >
              <!-- Header -->
              <div class="header">
                ${this.headerState === "loading"
                  ? html`
                      <!-- Loading Header -->
                      <div class="header-loading">
                        <div class="avatar skeleton"></div>

                        <div>
                          <div class="title muted">Loading agent…</div>
                          <div class="status muted">Connecting…</div>
                        </div>
                      </div>
                    `
                  : nothing}
                ${this.headerState === "error"
                  ? html`
                      <!-- Error Header -->
                      <div class="header-error">
                        <div class="avatar warning">⚠️</div>

                        <div>
                          <div class="title muted">Unavailable</div>
                        </div>
                      </div>
                    `
                  : nothing}
                ${this.headerState === "ready"
                  ? html`
                      <!-- Ready Header -->
                      <div class="header-ready">
                        <!-- Avatar -->
                        <div
                          style="
          width:34px;
          height:34px;
          border-radius:999px;
          overflow:hidden;
          background:#f1f1f1;
          display:grid;
          place-items:center;
        "
                        >
                          ${s.agent?.avatar
                            ? html`
                                <img
                                  src=${s.agent.avatar}
                                  alt=""
                                  style="width:100%;height:100%;object-fit:cover;"
                                />
                              `
                            : html`
                                <div
                                  style="
                  width:100%;
                  height:100%;
                  display:grid;
                  place-items:center;
                  font-weight:700;
                  font-size:12px;
                  color: var(--cg-on-primary);
                  background: var(--cg-primary);
                "
                                >
                                  ${s.agent?.name?.slice(0, 2).toUpperCase() ??
                                  "AI"}
                                </div>
                              `}
                        </div>

                        <!-- Meta -->
                        <div style="min-width:0;">
                          <div
                            style="font-weight:700;font-size:14px;line-height:1.1;"
                          >
                            ${s.agent?.name}
                          </div>

                          <div style="display:flex;align-items:center;gap:6px;">
                            <span
                              style="
              width:8px;
              height:8px;
              border-radius:999px;
              background:${s.online ? "#22c55e" : "#ef4444"};
              display:inline-block;
            "
                            ></span>

                            <span style="opacity: 0.8; font-size: 13px;">
                              ${s.online ? "Online" : "Offline"}
                            </span>
                          </div>
                        </div>

                        <!-- Actions -->
                        <div class="header-actions">
                          ${s.screen === "chat"
                            ? html`
                                <cg-overflow-menu
                                  .disabled=${isBusy}
                                  .onAction=${(
                                    action:
                                      | "new"
                                      | "recent"
                                      | "end"
                                      | "details",
                                  ) => {
                                    if (isBusy) return;
                                    if (action === "new") {
                                      this.salutation = this.pickSalutation();
                                      this.controller.startNewChat();
                                    }
                                    if (action === "recent")
                                      this.controller.setScreen("recent");
                                    if (action === "end")
                                      this.controller.startNewChat();
                                    if (action === "details")
                                      this.controller.setScreen("details");
                                  }}
                                ></cg-overflow-menu>
                              `
                            : html`
                                <sl-icon-button
                                  class="header-new-icon"
                                  name="plus-lg"
                                  label="Start new chat"
                                  style="font-size: 22px"
                                  @click=${() => {
                                    this.salutation = this.pickSalutation();
                                    this.controller.startNewChat();
                                  }}
                                ></sl-icon-button>
                              `}
                          ${this.config.defaultOpen
                            ? nothing
                            : html`
                                <button
                                  @click=${() => this.controller.close()}
                                  aria-label="Close"
                                  title="Close"
                                >
                                  ✕
                                </button>
                              `}
                        </div>
                      </div>
                    `
                  : nothing}
              </div>

              <!-- Body -->
              <div class="body screen-wrap">
                ${s.loading
                  ? html`
                      <div class="loader-wrap">
                        <sl-spinner
                          style="font-size: 40px;--indicator-color: ${s.agent
                            ?.primaryColor};--track-width: 4px;"
                        ></sl-spinner>
                      </div>
                    `
                  : nothing}
                ${s.error
                  ? html`
                      <div class="error-card error-${s.error.kind}">
                        <div class="error-icon">
                          ${s.error.kind === "offline" ? "📡" : "⚠️"}
                        </div>

                        <div class="error-content">
                          <div class="error-title">${s.error.title}</div>

                          <div class="error-description">
                            ${s.error.description}
                          </div>

                          ${s.error.recoverable
                            ? html`
                                <button
                                  class="error-action"
                                  @click=${() => this.controller.retryError()}
                                >
                                  ${s.error.actionLabel ?? "Retry"}
                                </button>
                              `
                            : nothing}
                        </div>
                      </div>
                    `
                  : nothing}
                ${!s.loading && !s.error
                  ? s.screen === "details"
                    ? html`
                        <cg-visitor-details-screen
                          .onBack=${() => this.controller.setScreen("chat")}
                          .onSubmit=${(name: string, email: string) =>
                            void this.controller.updateVisitorDetails(
                              name,
                              email,
                            )}
                        ></cg-visitor-details-screen>
                      `
                    : html`
                        <div
                          class="screen-track ${s.screen === "recent"
                            ? "track-recent"
                            : "track-chat"}"
                        >
                          <!-- Chat Screen -->
                          <div class="screen">
                            <cg-message-list
                              .messages=${s.messages}
                              .streaming=${!!s.streaming?.active}
                              .streamingText=${s.streaming?.content ?? ""}
                              .suggestedMessages=${s.agent?.suggestedMessages ??
                              []}
                              .showSuggestions=${s.showSuggestions}
                              .escalated=${!!s.escalated}
                              @suggestion-select=${(e: CustomEvent) => {
                                this.controller.setInput(e.detail.value);
                                this.controller.submitInput();
                              }}
                              @escalate=${() =>
                                void this.controller.escalateConversation()}
                              @submit-details=${(e: CustomEvent) =>
                                void this.controller.updateVisitorDetails(
                                  e.detail.name,
                                  e.detail.email,
                                )}
                              @confirm-booking=${(e: CustomEvent) =>
                                void this.controller.bookSchedulingSlot(
                                  e.detail.messageId,
                                  e.detail.slot,
                                  e.detail.attendee,
                                )}
                            ></cg-message-list>
                          </div>

                          <!-- Recent Screen -->
                          <div class="screen">
                            <cg-recent-chats
                              .items=${s.conversations}
                              .activeId=${s.conversationId}
                              .agentName=${s.agent?.name}
                              .agentAvatar=${s.agent?.widgetAvatar ??
                              s.agent?.avatar}
                              .onSelect=${(id: string) =>
                                this.controller.switchConversation(id)}
                              .onNew=${() => this.controller.startNewChat()}
                            ></cg-recent-chats>
                          </div>
                        </div>
                      `
                  : nothing}
                ${s.online
                  ? html``
                  : html`
                      <div
                        style="padding:8px 12px;border-bottom:1px solid var(--cg-border);background:rgba(255,0,0,0.06);"
                      >
                        <span class="muted"
                          >You’re offline. Messages will fail until you
                          reconnect.</span
                        >
                      </div>
                    `}
              </div>

              <!-- Footer (chat screen only) -->
              ${s.screen === "chat"
                ? html`
                    <div class="footer">
                      <cg-composer
                        .value=${s.input}
                        .closed=${String(s.conversationStatus).toUpperCase() ===
                        "CLOSED"}
                        .disabled=${!s.agent ||
                        s.loading ||
                        !!s.error ||
                        s.sending ||
                        !s.online}
                        .placeholder=${!s.agent || s.loading
                          ? "Loading agent…"
                          : s.error
                            ? "Chat unavailable"
                            : !s.online
                              ? "You’re offline"
                              : "Message…"}
                        .onInput=${(v: string) => this.controller.setInput(v)}
                        .onSubmit=${() => this.controller.submitInput()}
                        .onNewChat=${() => {
                          this.salutation = this.pickSalutation();
                          this.controller.startNewChat();
                        }}
                      ></cg-composer>

                      <div class="footer-meta">
                        ${this.config.showBranding
                          ? html`
                              <div class="branding">
                                Powered by
                                <strong
                                  ><a href="https://chatgrow.co"
                                    >Chatgrow.co</a
                                  ></strong
                                >
                              </div>
                            `
                          : nothing}
                      </div>
                    </div>
                  `
                : nothing}
            </div>
          `
        : nothing}
    `;
  }
}
